/**
 * The app's `QueryClient`, and the one adapter between `@repo/client/data`'s
 * plain option objects and TanStack Query (plan D16).
 *
 * `@repo/client` never imports `@tanstack/*`: it hands out `QueryDef`,
 * `MutationDef` and `InfiniteDef`, written by hand to the shapes TanStack
 * accepts. This file is the ONE place that names both sides, so a TanStack
 * upgrade that renames a field is a compile error here and nowhere else.
 */
import { ApiError, SessionExpiredError } from "@repo/client/api";
import type {
  InfiniteDef,
  MutationDef,
  QueryDef,
  QueryKey,
} from "@repo/client/data/core";
import {
  infiniteQueryOptions,
  QueryClient,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

declare module "@tanstack/react-query" {
  interface Register {
    queryMeta: {
      /**
       * Never written to the persisted cache (`persist.ts`). Set it on any
       * query that holds a credential or a one-shot ticket — the WebSocket
       * ticket, anything token-shaped. IndexedDB is readable by any script on
       * the origin and survives sign-out until the next save.
       */
      readonly noPersist?: boolean;
    };
  }
}

/** Seven days. Shared by `gcTime` here and the persister's `maxAge`. */
export const CACHE_MAX_AGE_MS = 7 * 24 * 3_600_000;

/**
 * How long a read counts as fresh (`D190`). Five minutes, not TanStack's
 * zero and not the thirty seconds this was: every app open and every return
 * from the background refetched each visible query older than that — a dozen
 * reads on Today, two dozen with the header and the trainer's — and each one
 * costs a round trip from Brazil. The writes that matter do not wait on it:
 * a write invalidates what it changed (`MutationDef.invalidates`, the
 * outbox), and the trainer's turns invalidate what its tools may have
 * written (`coach/after-turn.ts`).
 */
export const DEFAULT_STALE_MS = 5 * 60_000;

/** How many times a read is retried after its first failure. */
export const MAX_QUERY_RETRIES = 3;

/**
 * Whether a failed read is worth another attempt.
 *
 * - A network failure (fetch throws) and a 5xx: yes — the next attempt may
 *   land.
 * - Any 4xx: no. The server read the request and refused it; the same request
 *   gets the same answer, and a 404 retried three times is three round trips
 *   before the screen can say "not found".
 * - `SessionExpiredError`: no. The refresh already failed inside `ApiClient`;
 *   only a sign-in fixes it.
 * - An `ApiError` with a 2xx status is `readJson` failing to decode a success:
 *   a shape this build does not understand. Retrying cannot change it.
 */
export function shouldRetryQuery(
  failureCount: number,
  error: unknown
): boolean {
  if (failureCount >= MAX_QUERY_RETRIES) {
    return false;
  }
  if (error instanceof SessionExpiredError) {
    return false;
  }
  if (error instanceof ApiError) {
    return error.status >= 500;
  }
  return true;
}

/**
 * - `networkMode: "offlineFirst"`: run the queryFn once even when the manager
 *   says offline, so the persisted cache and any service-worker cache get a
 *   chance, and pause only the RETRIES. `online.ts` is what makes "offline"
 *   mean the device's network, not the browser's `online` event.
 * - `gcTime` MUST be at least the persister's `maxAge`. A restored query is an
 *   ordinary inactive query; with the 5-minute default it is garbage-collected
 *   five minutes after restore, and the next save persists a cache without it
 *   (TanStack persist-client docs, "gcTime").
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      mutations: { networkMode: "offlineFirst" },
      queries: {
        gcTime: CACHE_MAX_AGE_MS,
        networkMode: "offlineFirst",
        retry: shouldRetryQuery,
        staleTime: DEFAULT_STALE_MS,
      },
    },
  });
}

/** The app's one client. `provider.tsx` mounts it. */
export const queryClient = createQueryClient();

// ── The adapter ─────────────────────────────────────────────────────────────

/** What {@link unavailable} reads of a query result. */
export interface QueryAvailability {
  readonly data: unknown;
  readonly fetchStatus: "fetching" | "idle" | "paused";
  readonly isError: boolean;
}

/**
 * Nothing to draw and nothing coming (`D194`): no data, and the read failed
 * OR is paused. Under `networkMode: "offlineFirst"` a first read offline
 * runs once, fails, and PAUSES its retries — it then reports neither data
 * nor an error, and a screen that tests `isError` alone draws its skeleton
 * forever. This is the one test every screen's "could not load, try again"
 * branch uses instead, so offline with nothing cached reads as that rather
 * than as loading.
 */
export function unavailable(query: QueryAvailability): boolean {
  return (
    query.data === undefined &&
    (query.isError || query.fetchStatus === "paused")
  );
}

/** The TanStack options a screen may add to a `QueryDef`. */
export interface QueryExtra<T> {
  readonly enabled?: boolean;
  readonly meta?: { noPersist?: boolean };
  /** Drawn while the read is pending; never cached, never persisted. */
  readonly placeholderData?: () => T | undefined;
}

/**
 * A `QueryDef` from `@repo/client/data/*` as TanStack `queryOptions`.
 * An identity in behaviour; it exists so the boundary has a name and a type
 * check. Pass extra TanStack options (`enabled`, `meta`, `placeholderData`)
 * through `extra`.
 */
export function fromQueryDef<T>(def: QueryDef<T>, extra: QueryExtra<T> = {}) {
  const { placeholderData, ...rest } = extra;
  return queryOptions({
    ...rest,
    // TanStack types it over `NonFunctionGuard<T>`, which it does not export
    // and which no other conditional type over an open `T` is assignable to.
    // A `QueryDef`'s data is decoded JSON, never a function, so the guard
    // has nothing to refuse here.
    ...(placeholderData === undefined
      ? {}
      : { placeholderData: placeholderData as never }),
    queryFn: ({ signal }) => def.queryFn({ signal }),
    queryKey: def.queryKey,
    ...(def.staleTime === undefined ? {} : { staleTime: def.staleTime }),
  });
}

/** An `InfiniteDef` as TanStack `infiniteQueryOptions`. */
export function fromInfiniteDef<T, P>(def: InfiniteDef<T, P>) {
  return infiniteQueryOptions({
    getNextPageParam: (lastPage: T) => def.getNextPageParam(lastPage),
    initialPageParam: def.initialPageParam,
    queryFn: ({ pageParam, signal }) =>
      def.queryFn({ pageParam: pageParam as P, signal }),
    queryKey: def.queryKey,
    ...(def.staleTime === undefined ? {} : { staleTime: def.staleTime }),
  });
}

/** `useQuery` over a `QueryDef`. */
export function useDataQuery<T>(def: QueryDef<T>, extra: QueryExtra<T> = {}) {
  return useQuery(fromQueryDef(def, extra));
}

/** Invalidate each prefix. Awaited together, never one after another. */
export async function invalidateKeys(
  client: Pick<QueryClient, "invalidateQueries">,
  keys: readonly QueryKey[]
): Promise<void> {
  await Promise.all(
    keys.map((queryKey) => client.invalidateQueries({ queryKey }))
  );
}

/**
 * `useMutation` over a `MutationDef`: on success, every prefix in
 * `def.invalidates` is invalidated. The list lives beside the write in
 * `@repo/client`, so a screen never decides what a write changed.
 *
 * The invalidation is STARTED, not awaited (`D190`): `mutateAsync` resolves
 * when the server has answered, not when every screen that read the changed
 * data has refetched it. Awaited, a rename or a remove waited for the plan,
 * the draft, the progress and the change feed's pages to come back before
 * its sheet could close — four or five more round trips. The queries are
 * already marked stale and their refetch already on the wire when this
 * resolves, so a caller that must see the new data reads it with
 * `fetchQuery`, or joins the refetch with `refetchQueries(…, {
 * cancelRefetch: false })`, and is handed that same request.
 *
 * NOT for the workout's session and set writes: those go through the outbox
 * (`outbox-runner.ts`), because a paused mutation does not survive a restart.
 */
export function useDataMutation<I, O>(
  def: MutationDef<I, O>,
  options: {
    /**
     * Resolve only once the refetch has landed — for a screen whose very next
     * frame is DECIDED by the data the write changed (the body consent gate),
     * which would otherwise draw the old decision for one round trip.
     */
    readonly awaitRefetch?: boolean;
  } = {}
) {
  const client = useQueryClient();
  const awaitRefetch = options.awaitRefetch === true;
  return useMutation({
    mutationFn: def.mutationFn,
    mutationKey: def.mutationKey,
    onSuccess: () =>
      invalidateAfterWrite(client, def.invalidates ?? [], awaitRefetch),
  });
}

/**
 * `useDataMutation`'s `onSuccess`: invalidate `keys`, and hand back the
 * refetch only when the caller asked to wait for it. Otherwise `undefined`,
 * which is what lets `mutateAsync` resolve at the server's answer.
 */
export function invalidateAfterWrite(
  client: Pick<QueryClient, "invalidateQueries">,
  keys: readonly QueryKey[],
  awaitRefetch: boolean
): Promise<void> | undefined {
  const refetched = invalidateKeys(client, keys);
  if (awaitRefetch) {
    return refetched;
  }
  refetched.catch((error: unknown) => {
    console.warn("[data] invalidating after a write failed", error);
  });
}
